import { apiGet, apiPost, ApiError } from '../lib/api.ts'

/**
 * The Canvass as the website sees it (migrations 0104, 0106). Shared by the
 * Canvasses list, the wizard and the canvass page, so a state or a missing
 * piece reads the same everywhere.
 */

export type CanvassState = 'draft' | 'ready' | 'published' | 'completed' | 'deactivated'
export type Side = 'even' | 'odd' | 'all'

export interface CanvassRow {
  canvass_id: string
  name: string
  state: CanvassState
  missing: string[]
  end_date: string | null
  target_contacts: number | null
  list_status: string | null
  groups: number
  precincts: number | null
  doors: number | null
  voters: number | null
  walkers: number
  paper_groups: number
  created_at: string
  published_at: string | null
}

export interface GroupMember {
  user_id: string
  username: string
  display_name: string | null
  side: Side
}

export interface CanvassGroup {
  group_id: string
  name: string
  precinct_id: string | null
  mode: 'app' | 'paper'
  paper_walker: string | null
  doors: number
  voters: number
  members: GroupMember[]
}

export interface CanvassDetail {
  canvass_id: string
  campaign_id: string
  name: string
  description: string | null
  target_contacts: number | null
  end_date: string | null
  effective_end_date: string | null
  state: CanvassState
  missing: string[]
  who: { kind?: 'precincts' | 'area' | 'universe'; precincts?: string[]; turf_id?: string; turf_name?: string; universe_name?: string }
  created_at: string
  published_at: string | null
  list: { export_id: string; export_no: number; status: string; rows: number | null; rows_expire_at: string | null } | null
  survey: { survey_version_id: string; title: string; version: number } | null
  scripts: { script_id: string; title: string }[]
  totals: { doors: number; voters: number; precincts: number; ungrouped_doors: number }
  groups: CanvassGroup[]
}

export const STATE_LABEL: Record<CanvassState, string> = {
  draft: 'Draft',
  ready: 'Ready to publish',
  published: 'Published',
  completed: 'Completed',
  deactivated: 'Deactivated',
}

/** The old system's "Precincts Not Present", "Walkers Not Assigned"… in plain words. */
export const MISSING_LABEL: Record<string, string> = {
  no_list: 'Nobody chosen to walk yet',
  list_not_ready: 'The walk list is still being made',
  list_failed: 'The walk list could not be made — choose who to walk again',
  list_expired: 'The walk list has expired — choose who to walk again',
  no_groups: 'No groups yet',
  walkers_not_assigned: 'Walkers not assigned',
  ended: 'The end date has passed',
}

export const SIDE_LABEL: Record<Side, string> = { even: 'Even numbers', odd: 'Odd numbers', all: 'Both sides' }

export function message(caught: unknown): string {
  if (caught instanceof ApiError && caught.status === 403) {
    return caught.message && caught.message !== 'Request failed (403)' && caught.message !== 'forbidden'
      ? caught.message
      : 'You are not allowed to do that on this campaign.'
  }
  return caught instanceof Error ? caught.message : 'That did not work.'
}

/** Universe names are unique per campaign; a stamp keeps a second cut from colliding. */
function stamp(): string {
  return new Date().toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}

/**
 * WHO, the way the Turf page cuts a walk list: save a universe, read back the
 * version it pinned, cut a walk export from it. The export pins that version,
 * so who a canvass covers stays answerable after the universe changes.
 */
export async function cutWalkList(
  campaignId: string,
  canvassName: string,
  universe: { name: string; description: string; ast: unknown } | { universe_id: string },
): Promise<string> {
  const types = await apiGet<{ list_types: { list_type_id: string; code: string }[] }>(
    `/list-types?campaign_id=${campaignId}`,
  )
  const walk = types.list_types.find((x) => x.code === 'walk')
  if (!walk) throw new Error('This campaign has not bought walk lists.')

  const universeId =
    'universe_id' in universe
      ? universe.universe_id
      : (
          await apiPost<{ universe_id: string }>('/universes', {
            campaign_id: campaignId,
            name: `${universe.name} · ${stamp()}`,
            description: universe.description,
            ast: universe.ast,
          })
        ).universe_id
  const detail = await apiGet<{ universe_version_id: string }>(`/universes/${universeId}`)
  const cut = await apiPost<{ export_id: string }>('/exports', {
    campaign_id: campaignId,
    universe_version_id: detail.universe_version_id,
    list_type_id: walk.list_type_id,
    name: `Canvass — ${canvassName}`,
  })
  return cut.export_id
}

export const ui = {
  input: 'rounded-md border border-line bg-surface px-2 py-1.5 text-sm text-ink',
  label: 'mb-1 block text-xs font-medium tracking-wide text-ink-muted uppercase',
  primary:
    'rounded-md bg-brand-900 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-800 disabled:opacity-50',
  secondary:
    'rounded-md border border-line px-2.5 py-1.5 text-xs font-semibold hover:border-brand-400 disabled:opacity-50',
  link: 'text-brand-700 hover:underline',
}
